import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div className="max-w-sm mx-auto px-4 py-16 text-center">
        <AlertCircle className="text-rose-500 mx-auto mb-3" size={32} />
        <h1 className="text-xl font-bold mb-2">No reset token</h1>
        <p className="text-sm text-slate-600">
          This link is missing the reset token. Try clicking the link in your email again,
          or request a new one.
        </p>
        <Link to="/forgot-password" className="inline-block mt-4 text-sm text-brand underline">
          Request a new link
        </Link>
      </div>
    );
  }

  async function submit(e) {
    e.preventDefault();
    setError(null);

    if (password.length < 8 || !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setError('Password must be 8+ characters with uppercase, lowercase, and a number.');
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }

    setBusy(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.error === 'invalid_or_expired_token') {
          setError("This reset link has expired or is invalid. Request a new one.");
        } else {
          setError("Couldn't reset password. Try again.");
        }
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } finally {
      setBusy(false);
    }
  }

  if (success) {
    return (
      <div className="max-w-sm mx-auto px-4 py-16 text-center">
        <CheckCircle className="text-emerald-500 mx-auto mb-3" size={32} />
        <h1 className="text-xl font-bold mb-2">Password updated</h1>
        <p className="text-sm text-slate-600">Redirecting you to log in…</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-sm mx-auto px-4 py-12 space-y-4">
      <h1 className="text-2xl font-bold">Choose a new password</h1>
      <input
        type="password"
        required
        minLength={8}
        autoFocus
        placeholder="New password (8+ chars)"
        className="w-full border rounded px-3 py-2"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <input
        type="password"
        required
        minLength={8}
        placeholder="Confirm new password"
        className="w-full border rounded px-3 py-2"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />
      {error && (
        <div className="text-sm text-red-600 flex items-start gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      <button
        disabled={busy}
        className="w-full bg-brand text-white rounded py-2 font-medium disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Save new password'}
      </button>
      <div className="text-center text-sm">
        <Link to="/login" className="text-slate-500 hover:text-brand">Back to log in</Link>
      </div>
    </form>
  );
}
